'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Drives the demo slate's staggered row fade-in (globals.css `demo-row-in`).
 * Cycles the wrapper class pending → in → '' so rows mounted after the entrance
 * (sorts, scroll, selections) render normally without replaying it.
 */
export function useDemoRowEntrance<T extends HTMLElement>(entranceMs = 1400) {
  const ref = useRef<T>(null);
  const [phase, setPhase] = useState<'pending' | 'in' | 'done'>('pending');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      (entries, obs) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        obs.disconnect();
        setPhase('in');
        timer = setTimeout(() => setPhase('done'), entranceMs);
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [entranceMs]);

  return {
    ref,
    entranceClass:
      phase === 'pending' ? 'demo-rows-pending' : phase === 'in' ? 'demo-rows-in' : '',
  };
}
